import { useTranslations } from 'next-intl';
import { useAccount } from 'wagmi';

import CrownIcon from '@/assets/icons/account/crown.svg';
import FlashIcon from '@/assets/icons/account/flash.svg';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils/cn';
import { T_WinnersData } from '@/ts/types/T_WinnersData';

import { tabTriggerBaseStyles } from '../staticStyles';
import WinnerFlashCard from '../WinnerFlashCard';
import { I_Winner } from '../WinnerFlashCard';
import WinnerPrimeCard from '../WinnerPrimeCard';

interface I_ProfilePredictionDialogTabsProps {
  activeTab: 'prime' | 'flash';
  onTabChange: (value: 'prime' | 'flash') => void;
  defaultTab?: 'prime' | 'flash';
  primeData: T_WinnersData | null;
  flashData: T_WinnersData | null;
  isLoadingPrime: boolean;
  isLoadingFlash: boolean;
  transformWinners: {
    prime: I_Winner[];
    flash: I_Winner[];
  };
  userHasWonPrime: boolean;
  userHasWonFlash: boolean;
  shouldShowClaimButton: boolean;
  isAlreadyClaimed: boolean;
  isClaiming: boolean;
  onClaimWinnings: () => void;
  onLoadMoreFlash: () => void;
  onSelectFlashWinner: (winner: I_Winner) => void;
  decimals?: number;
}

export const ProfilePredictionDialogTabs = ({
  activeTab,
  onTabChange,
  primeData,
  flashData,
  isLoadingPrime,
  isLoadingFlash,
  transformWinners,
  userHasWonPrime,
  userHasWonFlash,
  shouldShowClaimButton,
  isAlreadyClaimed,
  isClaiming,
  onClaimWinnings,
  onLoadMoreFlash,
  onSelectFlashWinner,
  decimals = 2
}: I_ProfilePredictionDialogTabsProps) => {
  const t = useTranslations();
  const { address: connectedAddress } = useAccount();

  return (
    <Tabs
      defaultValue={activeTab}
      className="w-full"
      onValueChange={value => onTabChange(value as 'prime' | 'flash')}
    >
      <TabsList className="w-full bg-transparent p-0 h-auto">
        <TabsTrigger
          value="prime"
          className={cn(
            tabTriggerBaseStyles,
            'rounded-tl-lg rounded-tr-none rounded-bl-lg rounded-br-none'
          )}
        >
          <CrownIcon className="w-4 h-4" />
          <P className={cn('text-sm', activeTab === 'prime' && 'font-bold')}>
            {t('profile.predictionWinners.prime')} {primeData?.total ?? 0}
          </P>
          {userHasWonPrime && (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </TabsTrigger>

        <TabsTrigger
          value="flash"
          className={cn(
            tabTriggerBaseStyles,
            'rounded-tr-lg rounded-tl-none rounded-bl-none rounded-br-lg'
          )}
        >
          <FlashIcon className="w-4 h-4" />
          <P className={cn('text-sm', activeTab === 'flash' && 'font-bold')}>
            {t('profile.predictionWinners.flash')} {flashData?.total ?? 0}
          </P>
          {userHasWonFlash && (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </TabsTrigger>
      </TabsList>

      {(shouldShowClaimButton || isAlreadyClaimed) && (
        <div className="mt-4">
          <Button
            variant="brand"
            size="lg"
            className="w-full uppercase"
            onClick={isAlreadyClaimed ? undefined : onClaimWinnings}
            disabled={isAlreadyClaimed || isClaiming}
          >
            {isAlreadyClaimed
              ? t('profile.predictionWinners.claimed')
              : isClaiming
                ? t('profile.predictionWinners.claiming')
                : t('profile.predictionWinners.claimWin')}
          </Button>
        </div>
      )}

      <TabsContent value="prime" className="mt-4">
        {isLoadingPrime ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner size={24} />
          </div>
        ) : transformWinners.prime.length > 0 ? (
          <div className="space-y-3">
            <WinnerPrimeCard
              winners={transformWinners.prime}
              connectedAddress={connectedAddress}
              decimals={decimals}
            />
          </div>
        ) : (
          <div className="flex justify-center items-center py-8">
            <P className="text-muted-foreground">
              {t('profile.predictionWinners.noWinnersFound')}
            </P>
          </div>
        )}
      </TabsContent>

      <TabsContent value="flash" className="mt-4">
        {isLoadingFlash && !flashData ? (
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner size={24} />
          </div>
        ) : transformWinners.flash.length > 0 ? (
          <>
            <WinnerFlashCard
              onSelectFlashWinner={onSelectFlashWinner}
              winners={transformWinners.flash}
              connectedAddress={connectedAddress}
              decimals={decimals}
            />

            {flashData &&
              flashData.items.length < flashData.total &&
              !isLoadingFlash && (
                <div className="pt-6">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full uppercase"
                    onClick={onLoadMoreFlash}
                    disabled={isLoadingFlash}
                  >
                    {t('profile.predictionWinners.loadMore')}
                  </Button>
                </div>
              )}
            {isLoadingFlash && flashData && (
              <div className="flex justify-center items-center py-4">
                <LoadingSpinner size={24} />
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center items-center py-8">
            <P className="text-muted-foreground">
              {t('profile.predictionWinners.noWinnersFound')}
            </P>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};
